import {
  fetchEventSource,
  EventStreamContentType
} from '@microsoft/fetch-event-source'


import type { SSEEvent } from '../models/agent'

import { getBaseURL, getCustomHeaders } from './utils'


/** SSE 连接配置 */
export interface SSEClientOptions<T = SSEEvent> {
  /** 请求方法 */
  method?: 'GET' | 'POST'
  /** 请求体数据 */
  body?: unknown
  /** 收到消息回调 */
  onMessage: (event: T) => void
  /** 连接打开回调 */
  onOpen?: (response: Response) => void
  /** 错误回调 */
  onError?: (error: Error) => void
  /** 连接关闭回调 */
  onClose?: () => void
  /** 是否在页面隐藏时关闭连接（默认 true） */
  closeOnHidden?: boolean
  /** AbortController 用于取消请求 */
  signal?: AbortSignal
}

/** SSE 连接返回值 */
export interface SSEConnection {
  /** 中止连接 */
  abort: () => void
}


/** 自定义重试错误类，用于控制重试行为 */
class FatalError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FatalError'
  }
}

/**
 * SSE 客户端
 * 基于 @microsoft/fetch-event-source 封装
 */
export async function sseClient<T = SSEEvent>(
  url: string,
  options: SSEClientOptions<T>
): Promise<SSEConnection> {
  const {
    method = 'GET',
    body,
    onMessage,
    onOpen,
    onError,
    onClose,
    closeOnHidden = false,
    signal
  } = options

  const abortController = new AbortController()
  const fullUrl = url.startsWith('http') ? url : getBaseURL() + url

  // 合并外部 signal
  if (signal) {
    signal.addEventListener('abort', () => abortController.abort())
  }

  await fetchEventSource(fullUrl, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...getCustomHeaders()
    },
    body: body ? JSON.stringify(body) : undefined,
    signal: abortController.signal,
    openWhenHidden: !closeOnHidden,

    async onopen(response) {
      if (response.ok && response.headers.get('content-type')?.includes(EventStreamContentType)) {
        onOpen?.(response)
        return
      }

      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        const errorText = await response.text()
        throw new FatalError(`SSE connection failed: ${response.status} - ${errorText}`)
      }

      throw new Error(`SSE connection failed: ${response.status}`)
    },

    onmessage(msg) {
      if (msg.event === 'FatalError') {
        throw new FatalError(msg.data)
      }

      if (!msg.data || msg.data === '[DONE]') {
        return
      }

      try {
        const event = JSON.parse(msg.data) as T
        onMessage(event)
      } catch {
        // 忽略解析失败的消息
      }
    },

    onclose() {
      // 正常关闭时，中止连接防止重连
      abortController.abort()
      onClose?.()
    },

    onerror(err) {
      // FatalError 不重试
      if (err instanceof FatalError) {
        onError?.(err)
        throw err
      }

      // 其他错误也不重试
      onError?.(err instanceof Error ? err : new Error(String(err)))
      throw err
    }
  })

  return {
    abort: () => abortController.abort()
  }
}

/**
 * 创建 SSE 事件流的 AsyncIterable
 * 将回调式 API 转换为 AsyncIterable，保持与现有代码兼容
 */
export function createSSEStream<T = SSEEvent>(
  url: string,
  options: Omit<SSEClientOptions<T>, 'onMessage'>
): {
  stream: AsyncIterable<T>
  abort: () => void
} {
  const eventQueue: T[] = []
  let resolveNext: ((value: IteratorResult<T>) => void) | null = null
  let isDone = false
  let streamError: Error | null = null
  let abortFn: (() => void) | null = null

  const stream: AsyncIterable<T> = {
    [Symbol.asyncIterator]() {
      return {
        async next(): Promise<IteratorResult<T>> {
          if (streamError) {
            throw streamError
          }

          if (eventQueue.length > 0) {
            return { value: eventQueue.shift()!, done: false }
          }

          if (isDone) {
            return { value: undefined as unknown as T, done: true }
          }

          return new Promise<IteratorResult<T>>((resolve) => {
            resolveNext = resolve
          })
        }
      }
    }
  }

  sseClient<T>(url, {
    ...options,
    onMessage(event) {
      if (resolveNext) {
        resolveNext({ value: event, done: false })
        resolveNext = null
      } else {
        eventQueue.push(event)
      }
    },
    onClose() {
      isDone = true
      if (resolveNext) {
        resolveNext({ value: undefined as unknown as T, done: true })
        resolveNext = null
      }
      options.onClose?.()
    },
    onError(err) {
      streamError = err
      if (resolveNext) {
        resolveNext({ value: undefined as unknown as T, done: true })
        resolveNext = null
      }
      options.onError?.(err)
    }
  }).then(connection => {
    abortFn = connection.abort
  })

  return {
    stream,
    abort: () => abortFn?.()
  }
}
